import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Download, FileText, Trash2, MapPin, CheckCircle2, Upload, LayoutTemplate } from 'lucide-react';
import GSTTypeSelection from './components/GSTTypeSelection';
import PartyDetails from './components/PartyDetails';
import ProductTable from './components/ProductTable';
import HSNSummary from './components/HSNSummary';
import { GST_TYPES, INITIAL_SELLER, INITIAL_BUYER } from './utils/constants';
import { calculateInvoiceSummary, convertToWords } from './utils/taxCalculations';
import { generatePDF } from './utils/pdfGenerator';
import StandardTemplate from './components/templates/StandardTemplate';
import ModernTemplate from './components/templates/ModernTemplate';
import ClassicTemplate from './components/templates/ClassicTemplate';

function App() {
  const [gstType, setGstType] = useState(GST_TYPES.REGULAR);
  const [sellerDetails, setSellerDetails] = useState(INITIAL_SELLER);
  const [buyerDetails, setBuyerDetails] = useState(INITIAL_BUYER);
  const [shippingAddress, setShippingAddress] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('standard');
  const [logo, setLogo] = useState(null);
  const [isSameAddress, setIsSameAddress] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [showSignature, setShowSignature] = useState(true);
  const [products, setProducts] = useState([
    { id: 1, name: '', hsnCode: '', rate: 0, quantity: 1, gstRate: 18 }
  ]);
  const [scaleFactor, setScaleFactor] = useState(1);
  const previewRef = useRef(null);

  const isInvoiceValid = useMemo(() => {
    const hasSeller = !!(sellerDetails.name?.trim() && sellerDetails.address?.trim() && sellerDetails.state?.trim() && sellerDetails.gstNumber?.trim());
    const hasBuyer = !!(buyerDetails.name?.trim() && buyerDetails.address?.trim() && buyerDetails.state?.trim() && buyerDetails.gstNumber?.trim());
    const hasInvoiceInfo = !!(invoiceNumber?.trim() && invoiceDate?.trim());
    const hasProduct = products.some(p => p.name?.trim() && p.rate >= 1 && p.quantity > 0);
    return hasSeller && hasBuyer && hasInvoiceInfo && hasProduct;
  }, [sellerDetails, buyerDetails, invoiceNumber, invoiceDate, products]);

  useEffect(() => {
    const updateScale = () => {
      const containerWidth = window.innerWidth - 32;
      setScaleFactor(containerWidth < 794 ? containerWidth / 794 : 1);
    };
    window.addEventListener('resize', updateScale);
    updateScale();
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const invoiceSummary = useMemo(() => {
    return calculateInvoiceSummary(products, sellerDetails.gstNumber, buyerDetails.gstNumber, gstType);
  }, [products, sellerDetails.gstNumber, buyerDetails.gstNumber, gstType]);

  const amountInWords = useMemo(() => {
    return convertToWords(invoiceSummary.grandTotal);
  }, [invoiceSummary.grandTotal]);

  const handleDownloadPDF = async () => {
    if (!isPreview) {
      setIsPreview(true);
      setTimeout(async () => {
        await generatePDF('invoice-content');
      }, 500);
    } else {
      await generatePDF('invoice-content');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setLogo(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const isInterState = useMemo(() => {
    // If buyer GST is "NOT APPLICABLE", treat as intra-state (CGST/SGST)
    if (buyerDetails.gstNumber === 'NOT APPLICABLE') {
      return false;
    }
    const s = sellerDetails.gstNumber ? sellerDetails.gstNumber.substring(0, 2) : '';
    const b = buyerDetails.gstNumber ? buyerDetails.gstNumber.substring(0, 2) : '';
    return s !== b && s && b;
  }, [sellerDetails.gstNumber, buyerDetails.gstNumber]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="relative flex justify-between h-16 items-center max-w-[1600px] mx-auto">
            <div className="flex items-center gap-3 shrink-0">
              <img src="/logo.png" alt="Logo" className="h-6 w-auto object-contain" />
            </div>

            <div className="flex gap-2 sm:gap-3 items-center">
              {isPreview && (
                <div className="flex items-center bg-slate-100 rounded-md p-1 border border-slate-200">
                  <LayoutTemplate size={16} className="text-slate-500 mx-2" />
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="bg-transparent text-sm font-medium text-slate-700 outline-none cursor-pointer py-1 pr-2"
                  >
                    <option value="standard">Standard</option>
                    <option value="modern">Modern</option>
                    <option value="classic">Classic</option>
                  </select>
                </div>
              )}
              <button
                onClick={() => setIsPreview(!isPreview)}
                disabled={!isPreview && !isInvoiceValid}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${isPreview ? 'bg-slate-100 text-slate-700 hover:bg-slate-200' : isInvoiceValid ? 'bg-white text-blue-900 border border-blue-900 hover:bg-blue-50' : 'bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60'}`}
              >
                {isPreview ? "Edit Mode" : "Preview"}
              </button>
              <button
                onClick={handleDownloadPDF}
                disabled={!isInvoiceValid}
                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm transition-all ${isInvoiceValid ? 'bg-blue-900 text-white hover:bg-blue-800' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              >
                <Download size={16} className="mr-2" />
                <span className="hidden sm:inline">Download</span>
                <span className="sm:hidden">PDF</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {!isPreview ? (
          <div className="space-y-6 animate-in fade-in">
            <GSTTypeSelection selectedType={gstType} onChange={setGstType} isPreview={false} />

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-6 border-b pb-4">
                <FileText className="text-blue-900" size={20} />
                <h2 className="text-lg font-bold">Invoice Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Invoice Number *</label>
                  <input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="w-full px-4 py-2.5 border rounded-lg outline-none focus:border-blue-600" placeholder="INV-001" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Invoice Date *</label>
                  <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="w-full px-4 py-2.5 border rounded-lg outline-none focus:border-blue-600" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
                <PartyDetails title="Seller Details" details={sellerDetails} onChange={setSellerDetails} icon="store" isBillOfSupply={gstType === GST_TYPES.UNREGISTERED} allowNoGst={false} />
                <div className="mt-auto pt-6 border-t space-y-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input type="checkbox" checked={showSignature} onChange={(e) => setShowSignature(e.target.checked)} className="peer sr-only" />
                      <div className="w-5 h-5 border-2 rounded bg-white peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-all"></div>
                      <CheckCircle2 size={14} className="absolute top-0.5 left-0.5 text-white opacity-0 peer-checked:opacity-100" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">Include Signature</span>
                  </label>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Business Logo</label>
                    <div className="flex items-center gap-4">
                      {logo ? (
                        <div className="relative group">
                          <img src={logo} alt="Logo" className="h-20 w-20 object-contain border rounded-lg p-2 bg-slate-50" />
                          <button onClick={() => setLogo(null)} className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600"><Trash2 size={12} /></button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer hover:bg-slate-50 hover:border-blue-300 transition-all">
                          <Upload className="w-8 h-8 text-slate-400 mb-2" />
                          <span className="text-xs font-bold text-slate-600">Upload business logo</span>
                          <input type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <PartyDetails title="Buyer Details" details={buyerDetails} onChange={setBuyerDetails} icon="user" isBillOfSupply={gstType === GST_TYPES.UNREGISTERED} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="flex items-center gap-2 mb-6">
                <MapPin className="text-blue-600" size={20} />
                <h2 className="text-lg font-bold">Billing & Shipping Address</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Billing Address *</label>
                  <textarea value={buyerDetails.address} onChange={(e) => setBuyerDetails({ ...buyerDetails, address: e.target.value })} rows="3" className="w-full px-4 py-3 bg-slate-50 border rounded-lg outline-none focus:border-blue-600 resize-none" />
                  <label className="flex items-center gap-3 mt-4 cursor-pointer">
                    <input type="checkbox" checked={isSameAddress} onChange={(e) => { setIsSameAddress(e.target.checked); if (e.target.checked) setShippingAddress(''); }} className="h-5 w-5 rounded border-slate-300 shadow-sm" />
                    <span className="text-sm font-medium text-slate-600">Shipping same as billing</span>
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Shipping Address *</label>
                  <textarea value={isSameAddress ? buyerDetails.address : shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} disabled={isSameAddress} rows="3" className="w-full px-4 py-3 bg-slate-50 border rounded-lg outline-none focus:border-blue-600 resize-none disabled:bg-slate-100 disabled:text-slate-400" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <ProductTable products={products} setProducts={setProducts} isInterState={isInterState} gstType={gstType} isPreview={false} />
            </div>

            {gstType === 'Regular GST' && (
              <HSNSummary hsnSummary={invoiceSummary.hsnSummary} isBillOfSupply={gstType === GST_TYPES.UNREGISTERED} isInterState={isInterState} />
            )}

            <div className="flex flex-col lg:flex-row justify-between items-start gap-8 mt-4">
              <div className="w-full lg:w-1/2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Amount in Words</span>
                <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm text-sm font-medium text-slate-800">{amountInWords}</div>
              </div>
              <div className="w-full lg:w-5/12 bg-white p-6 rounded-xl shadow-sm border border-slate-200 space-y-3">
                <div className="flex justify-between text-sm text-slate-600">
                  <span>Taxable Amount</span>
                  <span className="font-bold">{formatCurrency(invoiceSummary.totalTaxable)}</span>
                </div>
                {gstType === 'Regular GST' && (
                  isInterState ? (
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>IGST Total</span>
                      <span className="font-bold">{formatCurrency(invoiceSummary.igst || 0)}</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>CGST Total</span>
                        <span className="font-bold">{formatCurrency(invoiceSummary.cgst || 0)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-slate-600">
                        <span>SGST Total</span>
                        <span className="font-bold">{formatCurrency(invoiceSummary.sgst || 0)}</span>
                      </div>
                    </>
                  )
                )}
                <div className="flex justify-between items-center text-xl font-bold text-blue-900 pt-4 border-t border-slate-100">
                  <span>Grand Total</span>
                  <span>{formatCurrency(invoiceSummary.grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full flex justify-center pb-8 overflow-hidden">
            <div id="invoice-content" style={{ transform: `scale(${scaleFactor})`, transformOrigin: 'top center', width: '794px', minWidth: '794px' }} className="min-h-[800px] shadow-2xl bg-white">
              {selectedTemplate === 'standard' && <StandardTemplate invoiceSummary={invoiceSummary} products={products} sellerDetails={sellerDetails} buyerDetails={buyerDetails} amountInWords={amountInWords} gstType={gstType} isInterState={isInterState} formatCurrency={formatCurrency} invoiceNumber={invoiceNumber} invoiceDate={invoiceDate} logo={logo} shippingAddress={shippingAddress} isSameAddress={isSameAddress} showSignature={showSignature} />}
              {selectedTemplate === 'modern' && <ModernTemplate invoiceSummary={invoiceSummary} products={products} sellerDetails={sellerDetails} buyerDetails={buyerDetails} amountInWords={amountInWords} gstType={gstType} isInterState={isInterState} formatCurrency={formatCurrency} invoiceNumber={invoiceNumber} invoiceDate={invoiceDate} logo={logo} shippingAddress={shippingAddress} isSameAddress={isSameAddress} showSignature={showSignature} />}
              {selectedTemplate === 'classic' && <ClassicTemplate invoiceSummary={invoiceSummary} products={products} sellerDetails={sellerDetails} buyerDetails={buyerDetails} amountInWords={amountInWords} gstType={gstType} isInterState={isInterState} formatCurrency={formatCurrency} invoiceNumber={invoiceNumber} invoiceDate={invoiceDate} logo={logo} shippingAddress={shippingAddress} isSameAddress={isSameAddress} showSignature={showSignature} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
